import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../lib/api';
import { REFRESH_INTERVAL_MS } from '../../lib/constants';
import { ApiError } from '../../lib/http';
import { ProductDetails } from '../../types/api';

const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Load one product and keep it fresh: on window focus (at most once per refresh interval) and
 * when its price is scheduled to change. A refresh keeps showing the current product while it loads.
 */
export function useProductDetails(itemId: string | undefined) {
  const [product, setProduct] = useState<ProductDetails | null>(null);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const loadedItem = useRef<string | undefined>(undefined);
  const lastRefresh = useRef(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    const initial = loadedItem.current !== itemId;
    if (initial) {
      setProduct(null);
      setError('');
      setUnavailable(false);
      setRefreshError('');
    }
    lastRefresh.current = Date.now();
    apiRequest<ProductDetails>(`/items/${encodeURIComponent(itemId ?? '')}`, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        loadedItem.current = itemId;
        setRefreshError('');
        setUnavailable(false);
        setProduct({ ...data, price: Number(data.price) });
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted) return;
        if (reason instanceof ApiError && (reason.status === 404 || reason.status === 422))
          setUnavailable(true);
        else if (initial) setError('Could not load this product. Please try again.');
        else setRefreshError('Could not refresh this product.');
      });
    return () => controller.abort();
  }, [itemId, attempt]);

  const nextPriceChange = product?.pricing?.next_change_at;
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastRefresh.current >= REFRESH_INTERVAL_MS) refresh();
    };
    window.addEventListener('focus', onFocus);
    const delay = nextPriceChange ? Date.parse(nextPriceChange) - Date.now() : NaN;
    const timer =
      Number.isFinite(delay) && delay >= 0 ? setTimeout(refresh, Math.min(delay, MAX_TIMEOUT_MS)) : undefined;
    return () => {
      window.removeEventListener('focus', onFocus);
      clearTimeout(timer);
    };
  }, [nextPriceChange, attempt, refresh]);

  return { product, error, refreshError, unavailable, refresh };
}
